import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  ComponentMultiStyleConfig,
  Icon,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { MainCommentBlockItemContent } from "../types";
import { CMFileUploadItem } from "../../file-upload";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { hasValue } from "../../../lib/util/common-util";
import { CMButton } from "../../base";

export type CMMainCommentBlockItemProps = {
  /**
   * コメントスレッドID
   */
  commentThreadId: string;

  /**
   * 表示用のデータ
   */
  content: MainCommentBlockItemContent;

  /**
   * 未読フラグ
   * trueの場合: 未読
   */
  isUnread?: boolean;

  /**
   * 開閉ボタンイベント
   * @param commentId
   * @param isOpen 開閉状態
   * 開いている状態:true
   */
  onToggle: (commentId: string, isOpen: boolean) => void;

  /**
   * ファイルが選択されたときに、そのファイルを引数として呼び出される。
   * @param fileId
   * @param fileName ファイル名
   */
  onDownloadFile?: (fileId: string, fileName: string) => void;

  /**
   * 既読更新イベント
   * @param commentId
   */
  onRead?: (commentId: string) => void;
} & ComponentStyleProps;

/**
 * 送信者情報とメッセージ本文を表示する。
 *
 * メッセージエリアはデフォルト2行分表示。
 * 既読更新イベントが未定義の場合、ボタン非表示。
 */
export const CMMainCommentBlockItem = ({
  commentThreadId,
  content,
  isUnread = true,

  onToggle,
  onDownloadFile,
  onRead,

  sx,
  ...rest
}: CMMainCommentBlockItemProps) => {
  const styles = useMultiStyleConfig("CMMainCommentBlockItemStyles");

  /**
   * 開閉状態
   * trueの場合: 開いている状態
   */
  const [isOpen, setStatus] = useState<boolean>(false);

  /**
   * コメントエリアの開閉スタイル
   */
  const appliedCommentStyle = useMemo(
    () => (isOpen ? styles.commentBoxOpen : styles.commentBoxClose),
    [isOpen, styles],
  );

  /**
   * 開閉イベントの通知
   */
  const _onToggle = () => {
    const toggledValue = !isOpen;
    setStatus(toggledValue);
    onToggle(commentThreadId, toggledValue);
  };

  /**
   * 既読更新ボタンの表示/非表示判定用
   */
  const isReadEventEnabled = useMemo(() => {
    return hasValue(onRead) && isUnread;
  }, [onRead, isUnread]);

  /**
   * 既読更新イベントの通知
   */
  const _onRead = () => {
    onRead?.(commentThreadId);
  };

  return (
    <Box sx={{ ...styles.container, ...sx }} {...rest}>
      {/*ヘッダー*/}
      <Box sx={styles.headerArea}>
        {/*送信者の情報*/}
        <Box sx={styles.senderArea}>
          <Text sx={styles.senderLabel}>{content.senderLabel}</Text>
          <Text sx={styles.separator}> {content.separator}</Text>
          <Text sx={styles.senderName}>{content.senderName}</Text>
        </Box>
        {/*返信件数の情報*/}
        <Box sx={styles.replyInfoArea}>
          <Text>{content.replyCommentCount}</Text>
          <Text sx={styles.replyCommentLabel}>{content.replyCommentLabel}</Text>
        </Box>
      </Box>

      {/*メインエリア*/}
      <Box sx={styles.mainArea}>
        <Box sx={styles.commentArea}>
          {/*コメント本文*/}
          <Box sx={appliedCommentStyle}>
            <Text sx={styles.comment}>{content.mainText}</Text>
          </Box>
          {/*ファイル一覧*/}
          <Box sx={styles.filesArea}>
            {content.files.map((file, index) => (
              <CMFileUploadItem
                key={index}
                editMode={"readOnly"}
                fileItem={file}
                onDownload={onDownloadFile}
              />
            ))}
          </Box>
        </Box>

        {/*フッタ ボタンエリア*/}
        <Box sx={styles.buttonArea}>
          {/*開く・閉じるボタン*/}
          <Button
            colorScheme={"teal"}
            onClick={_onToggle}
            sx={{ ...styles.buttonBase, ...styles.buttonOpenClose }}
          >
            <Text sx={styles.buttonLabel}>
              {isOpen ? content.buttonLabelClose : content.buttonLabelOpen}
            </Text>
            <Icon
              as={isOpen ? IoIosArrowUp : IoIosArrowDown}
              sx={styles.buttonIcon}
            />
          </Button>

          {/*既読にするボタン*/}
          {isReadEventEnabled ? (
            <CMButton
              label={content.buttonLabelRead}
              colorScheme={"teal"}
              onClick={_onRead}
              sx={{ ...styles.buttonBase, ...styles.buttonRead }}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export const CMMainCommentBlockItemStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "headerArea",
    "senderArea",
    "senderLabel",
    "separator",
    "senderName",
    "replyInfoArea",
    "replyCommentLabel",
    "mainArea",
    "commentArea",
    "commentBoxOpen",
    "commentBoxClose",
    "comment",
    "filesArea",
    "buttonArea",
    "buttonBase",
    "buttonOpenClose",
    "buttonIcon",
    "buttonLabel",
    "buttonRead",
  ],
  baseStyle: {
    container: {},
    headerArea: {
      display: "flex",
      flexDirection: "row",
      color: "gray.500",
      borderWidth: "1px",
      borderLeftStyle: "none",
      borderRightStyle: "none",
      borderColor: "transparent",
      borderBottomColor: "gray.400",
    },

    senderArea: {
      display: "flex",
      flexDirection: "row",
      fontSize: "16px",
      width: "fit-content",
      whiteSpace: "pre-wrap",
    },
    senderLabel: {},
    separator: {
      marginLeft: "2px",
      whiteSpace: "nowrap",
    },
    senderName: {
      marginLeft: "5px",
    },

    replyInfoArea: {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      alignItems: "end",
      fontSize: "14px",
      marginTop: "10px",
      marginLeft: "auto",
      right: "0px",
    },

    replyCommentLabel: {
      whiteSpace: "nowrap",
    },

    mainArea: {
      position: "relative",
      marginTop: "14px",
      marginLeft: "10px",
    },

    commentArea: {},
    commentBoxOpen: {
      h: "fit-content",
      color: "gray.900",
      overflow: "hidden",
    },
    commentBoxClose: {
      color: "gray.900",
      maxH: "50px",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      // "-webkit-box-orient": "vertical",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      // "-webkit-line-clamp": "2",
      WebkitLineClamp: "2",
    },

    comment: {
      whiteSpace: "pre-wrap",
    },

    filesArea: {
      marginTop: "6px",
      marginLeft: "-3px",
    },

    buttonArea: {
      display: "flex",
      flexDirection: "row",
      marginTop: "8px",
    },

    buttonBase: {
      h: "24px",
      w: "100px",
      bgColor: "teal.500",
      fontSize: "12px",
    },
    buttonOpenClose: {},

    buttonLabel: {
      h: "fit-content",
      w: "fit-content",
      position: "absolute",
    },
    buttonIcon: {
      h: "12px",
      w: "12px",
      marginLeft: "auto",
      marginRight: "-10px",
    },

    buttonRead: {
      marginLeft: "auto",
      right: "0px",
    },
  },
};
